import { AppConstants } from "@/common";
import { ClientMessage } from "@ca.vmedia.tv/domains";
import { Services } from "@ca.vmedia.tv/services";
import type { ExposeInterface } from './types';
import type { AxiosError } from 'axios';
import { Logger } from '@/utils';

export const useClientMessagesStore = defineStore('ClientMessages', (): ExposeInterface => {

    const { $domains, $services } = useNuxtApp();

    const _messages: Ref<ClientMessage[]> = ref([]);
    const _interval: Ref<number> = ref(AppConstants.ApiMessagesRequestIntervalMS);
    const _isModalVisible: Ref<boolean> = ref(false);

    const allMessages: ComputedRef<ClientMessage[]> = computed(() => {
        return [..._messages.value]
            .sort((a, b) => (
                Date.parse(a.expires ?? "") - Date.parse(b.expires ?? "")
            ));
    });

    const lastMessage: ComputedRef<ClientMessage> = computed(() => {
        return _messages.value
            .reduce((acc, cur) => (
                (!acc || Date.parse(acc?.expires ?? "") - Date.parse(cur.expires ?? ""))
                    ? acc
                    : cur
            ), new ClientMessage);
    });

    const hasMessages: ComputedRef<boolean> = computed(() => {
        return _messages.value.length > 0;
    });

    const isModalVisible: ComputedRef<boolean> = computed((): boolean => _isModalVisible.value);

    const appendMessages = (messages: ClientMessage[]): void => {
        for (const message of messages) {
            if (!message.id) {
                continue;
            }
            const index = _messages.value.findIndex(({ id }) => id === message.id);
            if (index >= 0) {
                _messages.value.splice(index, 1, message);
            } else {
                _messages.value.push(message);
            }
        }
        _messages.value = _messages.value.filter((message) => Date.parse(message.sent ?? "") < Date.now() && Date.now() < Date.parse(message.expires ?? ""));
    };

    const deleteMessages = (messagesIds: number[]): void => {
        if (!Array.isArray(messagesIds)) {
            return;
        }
        messagesIds.forEach((messageId) => {
            _messages.value = _messages.value.filter(({ id }) => id !== messageId);
        });
    };

    const setInterval = (interval: number): void => {
        _interval.value = interval;
    };

    const clearAll = (): void => {
        _messages.value = [];
        _interval.value = AppConstants.ApiMessagesRequestIntervalMS;
    };

    const startReceiving = async (): Promise<void> => {
        for await (const messages of $services.clientMessages.startReceiving(_interval.value)) {
            appendMessages(messages);
        }
    };

    const stopReceiving = (): void => {
        try {
            Services.Instance.clientMessages.stopReceiving();
            clearAll();
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const markRead = async (messagesIds: number[]): Promise<void> => {
        if (!Array.isArray(messagesIds) || !messagesIds.length) {
            return;
        }

        try {
            await $domains.clientRepository.markMessagesAsRead(messagesIds);
            deleteMessages(messagesIds);
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const closeUnreadMessages = (messagesIds: number[]): void => {
        if (!Array.isArray(messagesIds) || !messagesIds.length) {
            return;
        }
        deleteMessages(messagesIds);
    };
    const setIsShowModal = (isShow: boolean): void => {
        _isModalVisible.value = isShow;
    };

    return {
        allMessages,
        lastMessage,
        hasMessages,
        isModalVisible,
        appendMessages,
        deleteMessages,
        setInterval,
        clearAll,
        startReceiving,
        stopReceiving,
        markRead,
        setIsShowModal,
        closeUnreadMessages,
    };
});